import { ConfirmModal } from "@components/modals/ConfirmModal";
import type { LucideIcon } from "lucide-react";
import { Loader2 } from "lucide-react";
import { useState } from "react";

/** DaisyUI button variant used for `btn-{variant}` class composition. */
type ButtonVariant = "primary" | "secondary" | "accent" | "info" | "success" | "warning" | "error";

export interface BulkActionButtonProps {
  /** Icon component to display */
  icon?: LucideIcon;
  /** Button label text */
  label: string;
  /** DaisyUI button variant */
  variant?: ButtonVariant;
  /** Callback when button is clicked */
  onClick: () => void;
  /** Confirmation message before executing action — if omitted, action fires immediately */
  confirmMessage?: string;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Whether the action is in progress */
  isLoading?: boolean;
}

/**
 * Individual action button for bulk operations.
 * Supports optional confirmation modal and loading state.
 */
export function BulkActionButton({
  icon: Icon,
  label,
  variant = "primary",
  onClick,
  confirmMessage,
  disabled = false,
  isLoading = false,
}: BulkActionButtonProps) {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const handleClick = () => {
    if (confirmMessage) {
      setIsConfirmOpen(true);
    } else {
      onClick();
    }
  };

  const handleConfirm = () => {
    setIsConfirmOpen(false);
    onClick();
  };

  return (
    <>
      <button
        type="button"
        className={`btn btn-sm btn-${variant} gap-2`}
        onClick={handleClick}
        disabled={disabled || isLoading}
      >
        {isLoading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          Icon && <Icon className="w-4 h-4" />
        )}
        {label}
      </button>

      {confirmMessage && (
        <ConfirmModal
          isOpen={isConfirmOpen}
          onClose={() => setIsConfirmOpen(false)}
          onConfirm={handleConfirm}
          title="Confirmation"
          message={confirmMessage}
          confirmText="Confirmer"
          cancelText="Annuler"
          variant="warning"
        />
      )}
    </>
  );
}
